import React, { useState, useEffect } from 'react';
import { AlertTriangle } from 'lucide-react';

interface InactivityWarningModalProps {
  isOpen: boolean;
  onClose: () => void;
  remainingSeconds: number;
  onTimeout?: () => void; // Callback when countdown reaches zero
}

const InactivityWarningModal: React.FC<InactivityWarningModalProps> = ({
  isOpen,
  onClose,
  remainingSeconds: initialRemainingSeconds,
  onTimeout,
}) => {
  const [remainingSeconds, setRemainingSeconds] = useState(
    initialRemainingSeconds,
  );

  useEffect(() => {
    setRemainingSeconds(initialRemainingSeconds);
  }, [initialRemainingSeconds, isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    console.log(
      'Starting countdown timer with',
      initialRemainingSeconds,
      'seconds',
    );

    const interval = setInterval(() => {
      setRemainingSeconds((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          console.log('Countdown reached zero, triggering logout');
          // Call onTimeout when countdown reaches zero
          if (onTimeout) {
            onTimeout();
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [isOpen, onTimeout, initialRemainingSeconds]);

  if (!isOpen) return null;

  const minutes = Math.floor(remainingSeconds / 60);
  const seconds = remainingSeconds % 60;
  const formattedTime = `${minutes}:${seconds.toString().padStart(2, '0')}`;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full">
        <h2 className="text-xl font-bold mb-4 text-yellow-500 flex items-center">
          <AlertTriangle className="h-5 w-5 mr-2" />
          Inactivity Warning
        </h2>
        <p className="mb-6">
          You've been inactive for 8 minutes. In{' '}
          <span className="font-bold">{formattedTime}</span>, you'll be logged
          out due to inactivity. Please click Continue Session button below to
          keep your session active.
        </p>
        <div className="flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
          >
            Continue Session
          </button>
        </div>
      </div>
    </div>
  );
};

export default InactivityWarningModal;
